'use client';

import type { ApiError } from '@/lib/api/ApiError';
import { type TQrCodeContent, type TQrCodeOptions, type TShortUrl } from '@shared/schemas';
import { createStore } from 'zustand/vanilla';
import { safeLocalStorage } from '@/lib/utils';

export type QrCodeGeneratorState = {
	id?: string;
	name?: string;
	config: TQrCodeOptions;
	content: TQrCodeContent;
	shortUrl?: TShortUrl;
	latestQrCode?: {
		name?: string | null;
		config: TQrCodeOptions;
		content: TQrCodeContent;
	};
	lastError?: ApiError;
	bulkMode: {
		isBulkMode: boolean;
		file?: File;
	};
};

export type QrCodeGeneratorActions = {
	updateName: (name: string) => void;
	updateConfig: (config: Partial<TQrCodeOptions>) => void;
	updateContent: (content: TQrCodeContent) => void;
	updateLatestQrCode: (
		latestQrCode:
			| {
					name?: string | null;
					config: TQrCodeOptions;
					content: TQrCodeContent;
			  }
			| undefined,
	) => void;
	updateLastError: (lastError: ApiError) => void;
	updateBulkMode: (isBulkMode: boolean, file?: File) => void;
	resetStore: () => void;
};

export type QrCodeGeneratorStore = QrCodeGeneratorState & QrCodeGeneratorActions;

export const createQrCodeGeneratorStore = (initState: QrCodeGeneratorState) => {
	// Check if we're in a browser environment
	if (typeof window !== 'undefined') {
		// Check for unsavedQrConfig in localStorage
		const savedConfig = safeLocalStorage.getItem('unsavedQrConfig');
		if (savedConfig) {
			try {
				const parsedConfig = JSON.parse(savedConfig) as Partial<TQrCodeOptions>;
				initState.config = {
					...initState.config,
					...parsedConfig,
				};
				// Clear the unsavedQrConfig from localStorage
				safeLocalStorage.removeItem('unsavedQrConfig');
			} catch (error) {
				console.error('Failed to parse unsavedQrConfig from localStorage:', error);
			}
		}

		const savedContent = safeLocalStorage.getItem('unsavedQrContent');
		if (savedContent) {
			try {
				const parsedContent = JSON.parse(savedContent) as TQrCodeContent;
				initState.content = {
					...initState.content,
					...parsedContent,
				};
				// Clear the unsavedQrContent from localStorage
				safeLocalStorage.removeItem('unsavedQrContent');
			} catch (error) {
				console.error('Failed to parse unsavedQrContent from localStorage:', error);
			}
		}
	}

	return createStore<QrCodeGeneratorStore>()((set) => ({
		...initState,
		updateName: (name) => set({ name }),
		updateConfig: (config) => {
			set((state) => ({
				config: {
					...state.config,
					...config,
					imageOptions: {
						...state.config.imageOptions,
						...config.imageOptions,
					},
					dotsOptions: {
						...state.config.dotsOptions,
						...config.dotsOptions,
					},
					backgroundOptions: {
						...state.config.backgroundOptions,
						...config.backgroundOptions,
					},
					cornersSquareOptions: {
						...state.config.cornersSquareOptions,
						...config.cornersSquareOptions,
					},
					cornersDotOptions: {
						...state.config.cornersDotOptions,
						...config.cornersDotOptions,
					},
				},
			}));
		},
		updateContent: (content) => set({ content }),
		updateLatestQrCode: (latestQrCode) => set({ latestQrCode }),
		updateLastError: (lastError) => set({ lastError }),
		updateBulkMode: (isBulkMode, file) =>
			set({
				bulkMode: {
					isBulkMode,
					file,
				},
			}),
		resetStore: () => set({ ...initState }),
	}));
};
